import { useQuery } from "@tanstack/react-query";
import {
	AlertCircle,
	Building2,
	ChevronLeft,
	ChevronRight,
	Loader2,
	MapPin,
	Search,
	Users,
} from "lucide-react";
import { useState } from "react";
import { Link, useNavigation, useSearchParams } from "react-router";
import { Skeleton } from "~/components/ui/skeleton";
import { companySlug } from "~/lib/company-profile";
import {
	type CompanySearchResult,
	fetchCompanySearch,
	shouldRetryCompanySearch,
} from "~/lib/company-search";
import { regionToSlug } from "~/lib/directory-utils";
import { trackEvent } from "~/lib/gtag";
import { generateMetaTags, SITE_URL } from "~/lib/seo";
import type { Route } from "./+types/search";

export function meta({ location }: Route.MetaArgs) {
	const params = new URLSearchParams(location.search);
	const q = params.get("q");

	const title = q ? `${q} – Výsledky hledání | FirmIndex` : "Vyhledávání firem | FirmIndex";
	const description = q
		? `Výsledky vyhledávání pro "${q}". Najděte finanční data, obchodní rejstřík a další informace o českých firmách.`
		: "Vyhledávejte české firmy podle názvu nebo IČO. Obchodní rejstřík, finanční údaje a další informace.";
	const canonical = q ? `${SITE_URL}/search?q=${encodeURIComponent(q)}` : `${SITE_URL}/search`;

	return generateMetaTags({ title, description, canonical, noindex: !q });
}

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
	ACTIVE: { label: "Aktivní", className: "badge badge-soft badge-success" },
	DISSOLVED: { label: "Zrušená", className: "badge badge-soft badge-error" },
	IN_LIQUIDATION: { label: "V likvidaci", className: "badge badge-soft badge-warning" },
	IN_INSOLVENCY: { label: "V insolvenci", className: "badge badge-soft badge-error" },
	SUSPENDED: { label: "Pozastavená", className: "badge badge-soft badge-warning" },
};

function CompanyCard({ company, isPending }: { company: CompanySearchResult; isPending: boolean }) {
	const status = STATUS_LABELS[company.status] ?? {
		label: company.status,
		className: "badge badge-soft",
	};
	const rSlug = company.region ? regionToSlug(company.region) : null;

	return (
		<div
			className="flex flex-col gap-3 card-surface rounded-xl p-4 transition-[box-shadow,opacity] hover:shadow-md"
			aria-busy={isPending}
		>
			<Link
				to={`/firma/${company.ico}-${companySlug(company.name)}`}
				prefetch="intent"
				className="flex items-start justify-between gap-3"
			>
				<div className="flex flex-col gap-1">
					<h3 className="text-sm font-semibold">{company.name}</h3>
					<p className="text-xs text-muted-foreground">
						IČO: <span className="font-mono">{company.ico}</span>
						{company.legalForm && <> | {company.legalForm}</>}
					</p>
				</div>
				<div className="flex items-center gap-2">
					{isPending && (
						<Loader2 className="h-4 w-4 animate-spin text-primary" aria-label="Načítám firmu" />
					)}
					<span className={status.className}>{status.label}</span>
				</div>
			</Link>
			<div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
				{company.city && (
					<span className="flex items-center gap-1">
						<MapPin className="h-3 w-3" />
						{company.city}
						{company.region && rSlug ? (
							<>
								,{" "}
								<Link to={`/directory/kraj/${rSlug}`} className="text-primary hover:underline">
									{company.region}
								</Link>
							</>
						) : (
							company.region && <>, {company.region}</>
						)}
					</span>
				)}
				{company.naceDescription && (
					<span className="flex items-center gap-1">
						<Building2 className="h-3 w-3" />
						{company.naceCode ? (
							<Link
								to={`/directory/odvetvi/${company.naceCode}`}
								className="text-primary hover:underline"
							>
								{company.naceDescription}
							</Link>
						) : (
							company.naceDescription
						)}
					</span>
				)}
				{company.employeeLabel && (
					<span className="flex items-center gap-1">
						<Users className="h-3 w-3" />
						{company.employeeLabel}
					</span>
				)}
			</div>
		</div>
	);
}

function ResultSkeleton() {
	return (
		<div className="flex flex-col gap-3 card-surface rounded-xl p-4">
			<div className="flex items-start justify-between">
				<div className="flex flex-col gap-1">
					<Skeleton className="h-4 w-48" />
					<Skeleton className="h-3 w-32" />
				</div>
				<Skeleton className="h-5 w-16 rounded-full" />
			</div>
			<div className="flex gap-4">
				<Skeleton className="h-3 w-24" />
				<Skeleton className="h-3 w-32" />
			</div>
		</div>
	);
}

const PAGE_LIMIT = 20;

export default function SearchPage() {
	const [searchParams, setSearchParams] = useSearchParams();
	const q = searchParams.get("q") ?? "";
	const page = Number(searchParams.get("page") ?? "1");

	const [inputValue, setInputValue] = useState(q);
	const navigation = useNavigation();

	const { data, status, fetchStatus, error, refetch } = useQuery({
		queryKey: ["companySearch", q, page],
		queryFn: ({ signal }) => fetchCompanySearch(q, page, PAGE_LIMIT, signal),
		enabled: q.trim().length >= 2,
		retry: shouldRetryCompanySearch,
		staleTime: 60_000,
	});
	const isSearching = fetchStatus === "fetching";
	const pendingCompanyIco = navigation.location?.pathname.match(/^\/firma\/(\d+)/)?.[1] ?? null;

	const totalPages = data ? Math.ceil(data.total / PAGE_LIMIT) : 0;

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		const trimmed = inputValue.trim();
		if (trimmed.length >= 2) {
			trackEvent("search", { search_term: trimmed });
			setSearchParams({ q: trimmed, page: "1" });
		}
	};

	const goToPage = (newPage: number) => {
		setSearchParams({ q, page: String(newPage) });
	};

	return (
		<div className="min-h-screen bg-background">
			{/* Search bar */}
			<div className="border-b border-gray-200 bg-card">
				<div className="mx-auto max-w-4xl px-4 py-4">
					<form onSubmit={handleSearch} className="flex items-center gap-2">
						<div className="flex flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-background px-3 py-2 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
							<Search className="h-4 w-4 text-muted-foreground" />
							<input
								type="text"
								value={inputValue}
								onChange={(e) => setInputValue(e.target.value)}
								placeholder="Hledejte firmy podle názvu nebo IČO..."
								aria-label="Název firmy nebo IČO"
								className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
							/>
						</div>
						<button
							type="submit"
							className="btn btn-primary btn-sm"
							disabled={inputValue.trim().length < 2 || isSearching}
							aria-busy={isSearching}
						>
							{isSearching && <Loader2 className="h-4 w-4 animate-spin" />}
							{isSearching ? "Hledám…" : "Hledat"}
						</button>
					</form>
				</div>
			</div>

			{/* Content */}
			<main className="mx-auto max-w-4xl px-4 py-6">
				<div className="flex flex-col gap-4">
					{/* Results count */}
					{q && data && (
						<p className="text-sm text-muted-foreground">
							{data.total === 0
								? "Žádné výsledky"
								: `${data.totalIsCapped ? "Více než " : ""}${data.total.toLocaleString("cs-CZ")} ${data.total === 1 ? "výsledek" : data.total < 5 ? "výsledky" : "výsledků"}`}
							{` pro \u201E${q}\u201C`}
						</p>
					)}

					{/* Loading */}
					{status === "pending" && isSearching && q.trim().length >= 2 && (
						<div className="flex flex-col gap-3">
							{["s1", "s2", "s3", "s4", "s5"].map((key) => (
								<ResultSkeleton key={key} />
							))}
						</div>
					)}

					{/* Error */}
					{status === "error" && (
						<div className="flex flex-col items-center gap-2 py-16 text-center">
							<AlertCircle className="h-12 w-12 text-destructive" />
							<p className="text-sm font-medium">Nepodařilo se načíst data</p>
							<p className="text-sm text-muted-foreground">{(error as Error).message}</p>
							<button type="button" className="btn btn-outline btn-sm" onClick={() => refetch()}>
								Zkusit znovu
							</button>
						</div>
					)}

					{/* Empty state */}
					{q.trim().length < 2 && (
						<div className="flex flex-col items-center gap-2 py-16 text-center">
							<Search className="h-12 w-12 text-muted-foreground" />
							<p className="text-sm text-muted-foreground">
								Zadejte alespoň dva znaky názvu firmy nebo IČO
							</p>
						</div>
					)}

					{/* No results */}
					{data && data.total === 0 && (
						<div className="flex flex-col items-center gap-2 py-16 text-center">
							<Building2 className="h-12 w-12 text-muted-foreground" />
							<p className="text-sm font-medium">Nic jsme nenašli</p>
							<p className="text-sm text-muted-foreground">
								Zkuste upravit hledaný výraz nebo zkontrolovat pravopis
							</p>
						</div>
					)}

					{/* Results */}
					{data && data.results.length > 0 && (
						<div className="flex flex-col gap-3">
							{data.results.map((company) => (
								<CompanyCard
									key={company.id}
									company={company}
									isPending={pendingCompanyIco === company.ico}
								/>
							))}
						</div>
					)}

					{/* Pagination */}
					{totalPages > 1 && (
						<div className="flex items-center justify-center gap-2 py-4">
							<button
								type="button"
								className="btn btn-ghost btn-sm"
								disabled={page <= 1}
								onClick={() => goToPage(page - 1)}
							>
								<ChevronLeft className="h-4 w-4" />
								Předchozí
							</button>
							<span className="text-sm text-muted-foreground">
								Strana {page} z {totalPages}
							</span>
							<button
								type="button"
								className="btn btn-ghost btn-sm"
								disabled={page >= totalPages}
								onClick={() => goToPage(page + 1)}
							>
								Další
								<ChevronRight className="h-4 w-4" />
							</button>
						</div>
					)}
				</div>
			</main>
		</div>
	);
}
